/**
 * _pa 및 _pa.utils 초기화 및 유틸리티 함수 주입
 */
((window) => {
  // 1. 객체 초기화 (기존 객체가 있으면 유지)
  window._pa = window._pa || {};
  window._pa.utils = window._pa.utils || {};

  /**
   * 유틸리티 정의 함수
   * @param {Object} utils - 기능을 주입할 대상 객체
   */
  const defineUtils = (utils) => {
    const EMAIL_REGEX = /[^a-zA-Z0-9_.^+\-@]/g;
    /** 하루(24시간)를 밀리초로 환산한 상수 */
    const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

    /**
     * 두 값의 동등성을 비교한다. 문자열의 경우 대소문자 무시 옵션을 제공한다.
     * @param {*} a - 비교할 첫 번째 값
     * @param {*} b - 비교할 두 번째 값
     * @param {boolean} [equalsIgnoreCase=true] - 문자열 비교 시 대소문자 무시 여부
     * @returns {boolean} 두 값이 동등하면 true, 아니면 false
     */
    utils.isEquals = (a, b, equalsIgnoreCase = true) => {
      if ('string' === typeof a && 'string' === typeof b && equalsIgnoreCase) {
        return a.toLowerCase() === b.toLowerCase();
      }
      return a === b;
    };

    /**
     * 요소(Element)인지 확인
     * 단일 요소뿐만 아니라 NodeList, HTMLCollection 등 리스트 형태도 지원
     * @param {*} element
     * @returns {boolean}
     */
    utils.isElement = (element) => {
      try {
        if (null === element || 'undefined' === typeof element) {
          return false;
        }
        // 1. 단일 Element 확인
        if (element instanceof Element) {
          return true;
        }
        // 2. NodeList, HTMLCollection 등 컬렉션 확인
        if ('number' === typeof element.length) {
          if (0 === element.length) return false;
          // 모든 항목이 Element인지 검증
          return Array.prototype.every.call(element, ($node) => $node instanceof Element);
        }
        return false;
      } catch (e) {
        console.error('isElement error:', e, element);
        return false;
      }
    };

    /**
     * 데이터가 비어있는지 확인 (Element, Object, Array, String, Number 대응)
     * @param {*} data
     * @param {boolean} [isSpaceCheck=false] - 문자열일 경우 공백만 있는 경우도 비어있는 것으로 간주할지 여부
     * @returns {boolean}
     */
    utils.isEmpty = (data, isSpaceCheck = false) => {
      try {
        // 기본 null / undefined 체크
        if (null === data || 'undefined' === typeof data) {
          return true;
        }
        // Element 체크 (Element 타입이 들어오면 에러 발생)
        if (utils.isElement(data)) {
          throw 'Element is not support';
        }
        // Array, String, NodeList 등 길이를 가진 객체 처리
        if ('number' === typeof data.length) {
          const target = 'string' === typeof data && isSpaceCheck ? data.trim() : data;
          return 0 === target.length;
        }
        // Date 객체 체크 - getTime()이 NaN을 반환하면 유효하지 않은 날짜(Invalid Date)임
        if (data instanceof Date) {
          return Number.isNaN(data.getTime());
        }
        // Set, Map 체크 - 이들은 length 대신 size 속성을 가짐
        if (data instanceof Set || data instanceof Map) {
          return 0 === data.size;
        }
        // 순수 객체(Plain Object) 처리
        if ('object' === typeof data && data.constructor === Object) {
          return 0 === Object.keys(data).length;
        }
        // 숫자 처리 (NaN은 비어있는 것으로 간주)
        if ('number' === typeof data) {
          return Number.isNaN(data);
        }
        return !data;
      } catch (e) {
        console.error('isEmpty error:', e, data);
        return true; // 에러 발생 시 비어있는 것으로 간주하여 안전하게 처리
      }
    };

    /**
     * 유효한 숫자인지 확인 (숫자 형태의 문자열 포함)
     * @param {*} value - 확인할 값
     * @returns {boolean}
     */
    utils.isNumber = (value) => {
      try {
        if (null === value || 'undefined' === typeof value || '' === value) {
          return false;
        }
        const num = Number(value);
        return 'number' === typeof num && !Number.isNaN(num);
      } catch (e) {
        return false;
      }
    };

    /**
     * 이메일에서 허용되지 않는 문자 제거
     * 영문, 숫자, 그리고 _.^+-@ 특수문자만 허용
     * @param {string} email - 정제할 이메일
     * @returns {string} 허용된 문자만 남은 이메일
     * @example
     * sanitizeEmail('test한글@example.com'); // 'test@example.com'
     * sanitizeEmail('user+tag@mail.com');    // 'user+tag@mail.com'
     */
    utils.sanitizeEmail = (email) => {
      if (!email || typeof email !== 'string') {
        return '';
      }
      return email.trim().replace(EMAIL_REGEX, '');
    };

    /**
     * 불필요한 연속 이벤트(예: scroll)로 인한 성능 저하 방지
     * @param {HTMLElement} eventTarget - 기본값은 window 객체
     * @param {String} eventType - 이벤트 타입
     * @param {Number} delay - 첫 트리거 이후 동작을 실행하기까지 대기할 시간(ms), 숫자가 아니거나 음수면 100ms로 정규화
     * @param {boolean|AddEventListenerOptions} options - capture, passive, once 등, 없을 경우 scroll/touchmove/wheel에 대해 passive: true를 기본 적용
     * @param  {...any} actionFunc - 실행할 콜백 함수(하나 이상). 함수가 아닌 값은 필터링되어 무시됨.
     * @returns
     */
    utils.eventThrottler = (eventTarget = window, eventType, delay, options, ...actionFunc) => {
      const canListen =
        eventTarget && //
        'function' === typeof eventTarget.addEventListener &&
        'function' === typeof eventTarget.removeEventListener;

      if (!canListen || 'string' !== typeof eventType) {
        return;
      }

      const actions = actionFunc.filter((func) => 'function' === typeof func);

      if (0 === actions.length) {
        return;
      }

      const DEFAULT_DELAY = 100;
      const normalizedDelay = utils.isNumber(delay) && 0 <= Number(delay) ? Number(delay) : DEFAULT_DELAY;

      const PASSIVE_DEFAULT_EVENTS = ['scroll', 'touchmove', 'wheel'];
      const shouldPassiveDefault =
        PASSIVE_DEFAULT_EVENTS.includes(eventType) && //
        ('undefined' === typeof options || null === options);
      const listenerOptions = shouldPassiveDefault ? { passive: true } : options;

      let timer = null;

      const handler = (event) => {
        if (timer) return;

        timer = setTimeout(() => {
          timer = null;
          actions.forEach((func) => func(event));
        }, normalizedDelay);
      };

      eventTarget.addEventListener(eventType, handler, listenerOptions);

      const cleanUpThrottler = () => {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        eventTarget.removeEventListener(eventType, handler, listenerOptions);
      };

      return cleanUpThrottler;
    };

    /**
     * 스크롤 시 요소가 화면에 나타나면 클래스를 추가/제거하는 옵저버 초기화
     * @param {Object} params - 설정 옵션
     * @param {string} [params.selector='.js-scrollObserve'] - 감시 대상 선택자
     * @param {string} [params.activeClass='on'] - 활성화 시 추가될 클래스명
     * @param {boolean} [params.isOnce=false] - 한 번 활성화된 후 감시를 종료할지 여부
     * @param {boolean} [params.isMirror=false] - true면 위/아래 어디로 벗어나든 클래스 제거 (양방향), false면 아래로 벗어날 때만 제거 (단방향)
     * @param {number} [params.threshold=0.3] - 요소가 화면에 보이는 비율 (0~1)
     * @param {Element} [params.root=null] - 기준이 되는 부모 요소 (기본 viewport)
     * @param {string} [params.rootMargin='0px'] - 감지 범위 확장/축소 마진
     * @param {Function} [params.onEnter] - 화면 진입 시 실행할 콜백
     * @param {Function} [params.onLeave] - 화면 이탈 시 실행할 콜백
     * @returns {IntersectionObserver|null}
     */
    utils.scrollObserver = (params = {}) => {
      const {
        selector = '.js-scrollObserve', // 감시 대상 선택자
        activeClass = 'on', // 활성화 시 추가될 클래스명
        isOnce = false, // 한 번 활성화된 후 감시를 종료할지 여부
        isMirror = false, // true면 위/아래 어디로 벗어나든 클래스 제거 (양방향)
        threshold = 0.3, // 요소가 얼마나 보일 때 실행할지 (0~1)
        root = null, // 기준이 되는 부모 요소 (기본 viewport)
        rootMargin = '0px', // 감지 범위 확장/축소 마진
        onEnter, // 화면 진입 시 실행할 콜백
        onLeave // 화면 이탈 시 실행할 콜백
      } = params;

      const $items = document.querySelectorAll(selector);

      if (!utils.isElement($items)) {
        return null;
      }

      const onIntersection = (entries, observer) => {
        entries.forEach((entry) => {
          const $target = entry.target;

          if (entry.isIntersecting) {
            // 화면 진입 시 클래스 추가 및 콜백 실행
            $target.classList.add(activeClass);
            if ('function' === typeof onEnter) onEnter($target);
            // 한 번만 실행 옵션
            if (isOnce) observer.unobserve($target);
          } else {
            // isMirror가 true면 무조건 제거, false면 아래로 사라질 때만(0 < top) 제거
            const shouldRemove = isMirror || 0 < entry.boundingClientRect.top;

            if (shouldRemove) {
              $target.classList.remove(activeClass);
              if ('function' === typeof onLeave) onLeave($target);
            }
          }
        });
      };

      const observer = new IntersectionObserver(onIntersection, { root, rootMargin, threshold });
      $items.forEach(($item) => observer.observe($item));
      return observer;
    };

    /**
     * 기기 환경 정보를 초기화하고 HTML 클래스에 반영한다.
     * @description iPadOS 데스크탑 모드 감지 및 현대 브라우저 식별 로직 포함
     * @example _pa.device.isMobile
     * @returns {Object} 기기 및 브라우저 상태를 담은 객체
     */
    utils.device = (() => {
      try {
        const userAgent = navigator.userAgent;
        // navigator.userAgentData는 최신 브라우저(Chromium 기반) 지원용
        const platform = navigator.userAgentData?.platform || navigator.platform || '';
        const maxTouch = navigator.maxTouchPoints || 0;
        const isUaMobile = navigator.userAgentData?.mobile || false;
        // 기본 특성 감지 (터치 지원 여부)
        const isTouch = 0 < maxTouch || 'ontouchstart' in window;
        // 운영체제 및 기기 종류 감지
        // isAndroid 확인
        const isAndroid = /Android/i.test(userAgent);
        // iOS 확인: iPhone/iPad/iPod 또는 Macintosh이면서 터치가 되는 경우(iPadOS 데스크탑 모드)
        const isIOS = /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && isTouch);
        // 터치가 지원되지 않는 Macintosh만 순수 데스크탑 Mac으로 간주
        const isMac = /Mac/i.test(platform) && !isTouch;
        // Apple 기기 통합 판별 (iPhone + iPad + Mac)
        const isApple = isIOS || isMac;
        // 모바일 통합 확인 (Android, iOS 및 기타 모바일 OS)
        const isMobile = isAndroid || isIOS || /webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || isUaMobile;
        // 운영체제 확인
        const isWindows = /Win/i.test(platform);
        // 브라우저 감지
        const isEdge = /Edg/i.test(userAgent);
        const isSamsung = /SamsungBrowser/i.test(userAgent);
        const isFirefox = /Firefox|FxiOS/i.test(userAgent);
        // Chrome은 Edge/Samsung/Opera를 제외해야 정확함
        const isChrome = /Chrome|CriOS/i.test(userAgent) && !isEdge && !isSamsung && !/OPR|Opera/i.test(userAgent);
        /**
         * Safari는 UA에 'Safari' 토큰이 광범위하게 포함되어 오탐이 잦아
         * Version 토큰이 있는지 + 타 브라우저 토큰을 제외해 엄격히 판별한다.
         */
        const isSafari = /Safari/i.test(userAgent) && /Version\/\d+/i.test(userAgent) && !isChrome && !isEdge && !isSamsung && !isFirefox && !/OPR|Opera/i.test(userAgent);
        // App 내 브라우저
        const isInApp = /KAKAOTALK|NAVER|Instagram|FBAN|FBAV|Line/i.test(userAgent);

        /** @type {Object} 최종 기기 정보 객체 */
        const info = {
          isTouch,
          isMobile,
          isDesktop: !isMobile,
          isAndroid,
          isIOS,
          isWindows,
          isMac,
          isEdge,
          isSamsung,
          isFirefox,
          isChrome,
          isSafari,
          isApple,
          isInApp
        };

        // HTML 클래스 자동 부여 (CSS 대응용)
        const $root = document.documentElement;
        const BOOL_TYPE_PREFIX = 'is';
        Object.keys(info).forEach((key) => {
          // 'is'로 시작하고 값이 true인 경우만 클래스로 변환
          if (info[key] && BOOL_TYPE_PREFIX === key.slice(0, BOOL_TYPE_PREFIX.length)) {
            const className = `device_${key.slice(BOOL_TYPE_PREFIX.length).toLowerCase()}`;
            $root.classList.add(className);
          }
        });

        return info;
      } catch (e) {
        console.error('initDeviceInfo error:', e);
        return {};
      }
    })();

    /**
     * [내부 유틸] DOM 컬렉션 여부 확인 (NodeList, HTMLCollection)
     *
     * 목적 :
     * 1. 브라우저 환경에 따라 querySelectorAll은 NodeList를, getElementsByClassName은 HTMLCollection을 반환합니다.
     * 2. instanceof 체크가 실패할 수 있는 다양한 엣지 케이스(예: 다른 iframe의 요소)를 방지하기 위해
     *    Object.prototype.toString.call을 사용하여 객체의 내부 태그를 정확히 식별합니다.
     *
     * @param {*} value - 확인할 대상
     * @returns {boolean} DOM 컬렉션 여부
     */
    const isDomCollection = (value) => {
      if (!value) return false;

      // 1차: 표준 instanceof 체크 (대부분의 현대적 환경)
      if ('undefined' !== typeof NodeList && value instanceof NodeList) return true;
      if ('undefined' !== typeof HTMLCollection && value instanceof HTMLCollection) return true;

      // 2차: 크로스 프레임 혹은 구형 브라우저 대응을 위한 태그 체크
      const objectTag = Object.prototype.toString.call(value);
      return '[object NodeList]' === objectTag || '[object HTMLCollection]' === objectTag;
    };

    /**
     * 입력값을 Element 배열로 안전하게 변환
     *
     * 목적: jQuery처럼 단일 요소, 노드 리스트, CSS 선택자 문자열을 모두 수용하기 위함입니다.
     * 모든 입력을 배열로 정규화함으로써 내부 로직에서 단일 루프로 처리가 가능해집니다.
     *
     * @param {string|Element|NodeList|Array} $target - 대상 요소 혹은 선택자
     * @returns {Element[]} 정규화된 요소 배열
     */
    utils.normalizeElements = ($target) => {
      // 유효하지 않은 입력(null, undefined, 빈 문자열)에 대한 조기 리턴
      if (null === $target || 'undefined' === typeof $target || '' === $target) return [];

      // 1. 단일 Element 혹은 이미 정규화된 배열인 경우 (사내 유틸 우선 활용)
      // utils.isEmpty가 Element 타입 전달 시 에러를 발생시키므로 isElement로 사전 검증
      if (utils.isElement($target)) return $target instanceof Element ? [$target] : Array.from($target);

      // 2. CSS 선택자 문자열인 경우
      if ('string' === typeof $target) {
        try {
          // 잘못된 선택자가 입력되어도 전체 스크립트가 멈추지 않도록 예외 처리
          return Array.from(document.querySelectorAll($target));
        } catch (error) {
          return [];
        }
      }

      // 3. 네이티브 배열 혹은 DOM 컬렉션(NodeList, HTMLCollection)인 경우
      if (Array.isArray($target) || isDomCollection($target)) {
        // Array.from으로 유사 배열 객체를 실제 배열로 변환한 뒤,
        // 배열 내의 각 항목이 실제 DOM 요소인지 다시 한번 검증(Filter)합니다.
        // (텍스트 노드나 주석 등이 포함될 수 있는 엣지 케이스 방지)
        return Array.from($target).filter((n) => utils.isElement(n));
      }

      return [];
    };

    /**
     * 요소 가시 상태 확인
     * @param {Element} $element - 확인할 요소
     * @returns {boolean} 가시 여부
     */
    utils.isVisible = ($element) => {
      if (!utils.isElement($element)) return false;

      const computedStyle = window.getComputedStyle($element);
      if ('none' === computedStyle.display) return false;

      return !!($element.offsetWidth || $element.offsetHeight || $element.getClientRects().length);
    };

    /**
     * fetch API를 래핑하여 X-Requested-With 헤더를 자동으로 추가한다.
     * @param {string} url - 요청할 URL
     * @param {RequestInit} [options={}] - fetch options (method, headers, body 등)
     * @returns {Promise<Response>} fetch Promise
     */
    utils.fetch = (url, options = {}) => {
      const headers = new Headers(options.headers || {});
      headers.set('X-Requested-With', 'XMLHttpRequest');

      return fetch(url, {
        ...options,
        headers
      });
    };

    utils.cookie = (() => {
      /**
       * [내부 유틸] 구성에서 쿠키 도메인 후보를 안전하게 가져옴 (오타 호환)
       * 기존 프로젝트들에서는 _abyss.api.Http 로 authCookieDomain이 존재.
       * authCookieDomain이 authCookieDoamin으로 오타가 나있는 프로젝트도 존재.
       * 추후 _pa로의 확장성과 오타 부분을 고려하여 _configuredDomain 부분을 아래와 같이 잡음
       */
      const _configuredDomain =
        window._pa?.api?.Http?.authCookieDomain || window._pa?.api?.Http?.authCookieDoamin || window._abyss?.api?.Http?.authCookieDomain || window._abyss?.api?.Http?.authCookieDoamin || '';

      /**
       * [내부 유틸] 도메인 유효성 검증 및 정규화
       * - localhost, IP 환경에서는 도메인 생략
       * - 현재 호스트의 suffix인 경우에만 도메인 적용
       */
      const _normalizeDomain = (domain) => {
        if ('string' !== typeof domain || utils.isEmpty(domain)) return '';

        const host = location.hostname.toLowerCase();
        const candidate = domain.trim().toLowerCase().replace(/^\./, '');

        // localhost 또는 IP 주소 형태인 경우 domain 속성을 주지 않는 것이 가장 안전함
        if ('localhost' === candidate || /^[0-9.]+$/.test(candidate)) return '';

        // 현재 접속한 호스트와 일치하거나, 호스트의 서브도메인인 경우에만 허용
        if (candidate !== host && !host.endsWith(`.${candidate}`)) return '';

        return candidate;
      };

      const DEFAULT_DOMAIN = _normalizeDomain(_configuredDomain);

      return {
        /**
         * 쿠키 설정
         * @param {string} name 쿠키명
         * @param {any} value 값
         * @param {number} [days] 만료일 (미지정 시 세션 쿠키)
         * @param {string} [domain=DEFAULT_DOMAIN] 적용 도메인
         */
        set: (name, value, days, domain = DEFAULT_DOMAIN) => {
          if (utils.isEmpty(name)) return;

          // 기본 속성: Path, SameSite
          const parts = [`${name}=${encodeURIComponent(null == value ? '' : String(value))}`, 'path=/', 'SameSite=Lax'];

          // 만료일 처리
          if (utils.isNumber(days)) {
            const date = new Date();
            if (0 < days) {
              // 양수: 만료일 설정
              date.setTime(date.getTime() + days * MILLISECONDS_PER_DAY);
              parts.push(`expires=${date.toUTCString()}`);
            } else {
              // 0 또는 음수: 과거 날짜로 설정하여 즉시 삭제용
              parts.push(`expires=${new Date(0).toUTCString()}`);
            }
          } else {
            // 숫자 타입이 아닌 days 전달 시 경고 로그
            console.warn('[utils.cookie.set] "days" 값은 숫자(또는 숫자 형태의 문자열)이어야 합니다.', '전달값:', days, '→ expires 미지정(세션 쿠키)로 처리합니다.');
          }

          // 도메인 유효성 검증 후 추가
          const validDomain = _normalizeDomain(domain);
          if (!utils.isEmpty(validDomain)) {
            parts.push(`domain=${validDomain}`);
          }

          // 보안: HTTPS 환경이면 Secure 속성 자동 추가
          if ('https:' === location.protocol) {
            parts.push('Secure');
          }

          document.cookie = `${parts.join('; ')};`;
        },

        /**
         * 쿠키 조회
         * @param {string} name 쿠키명
         */
        get: (name) => {
          if (utils.isEmpty(name)) return '';

          const nameEQ = `${name}=`;
          const cookieList = (document.cookie || '').split(';');

          for (let i = 0; i < cookieList.length; i++) {
            const cookie = cookieList[i].trim();
            if (0 === cookie.indexOf(nameEQ)) {
              const rawValue = cookie.substring(nameEQ.length);
              try {
                return decodeURIComponent(rawValue);
              } catch (e) {
                return rawValue; // 디코딩 실패 시 원본 반환
              }
            }
          }
          return '';
        },

        /**
         * 쿠키 제거
         * @param {string} name 쿠키명
         * @param {string} [domain=DEFAULT_DOMAIN] 적용 도메인
         */
        remove: function (name, domain = DEFAULT_DOMAIN) {
          this.set(name, '', -1, domain);
        },

        /**
         * 쿠키 존재 여부 확인
         * @param {string} name 쿠키명
         */
        has: function (name) {
          // 빈 문자열이 아니면 존재하는 것으로 간주
          return '' !== this.get(name);
        }
      };
    })();

    return utils;
  };

  // 2. 임시 객체를 만들어 기능을 정의한 후, 두 곳에 모두 주입 (참조 공유)
  const utilsFunctions = {};
  defineUtils(utilsFunctions);

  // 3. Object.assign으로 기존 속성을 유지하며 공존 시킴
  Object.assign(window._pa.utils, utilsFunctions);
  Object.assign(window._pa, utilsFunctions);
})(window);
